// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button } from '@mui/material';
import { AppIcon, CloseButton, Heading, Tabs } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import styles from './silver-alpha-topbar.module.css';
import { useSilverAlphaTopbar } from './use-silver-alpha-topbar';

export const SilverAlphaTopbar = defineComponent({
  displayName: 'SilverAlphaTopbar',
  useSetup: useSilverAlphaTopbar,
  View: ({
    LeaveModalRenderer,
    activeTab,
    hasActiveProposal,
    titleKey,
    onBackClick,
    onCancelLeave,
    onConfirmLeave,
    onSelectHome,
    onSelectDocuments,
  }) => (
    <>
      <header className={styles.bar} data-testid="silver-alpha-topbar">
        <div className={styles.titles}>
          <Heading
            title={<Translation i18nKey={titleKey} ns="components" />}
            variant="h2"
          />
          <p className={styles.subtitle}>
            <Translation i18nKey="silver-alpha-topbar.subtitle" ns="components" />
          </p>
          <Tabs.Root
            baseId="silver-alpha-tabs"
            className={styles.tabs}
            controlled
            data-testid="silver-alpha-tabs"
            defaultValue={activeTab}
          >
            <Tabs.List>
              <Tabs.Trigger
                data-testid="silver-alpha-tab-home"
                onClick={onSelectHome}
                value="home"
              >
                <Translation i18nKey="silver-alpha-tabs.home" ns="components" />
              </Tabs.Trigger>
              {hasActiveProposal ? (
                <Tabs.Trigger
                  data-testid="silver-alpha-tab-documents"
                  onClick={onSelectDocuments}
                  value="documents"
                >
                  <Translation
                    i18nKey="silver-alpha-tabs.documents"
                    ns="components"
                  />
                </Tabs.Trigger>
              ) : null}
            </Tabs.List>
          </Tabs.Root>
        </div>
        <div className={styles.actions}>
          <Button
            className={styles.back}
            color="secondary"
            data-testid="silver-alpha-topbar-back-button"
            onClick={onBackClick}
            startIcon={<AppIcon icon="ArrowLeft" size="sm" />}
            sx={{ textTransform: 'none' }}
            variant="outlined"
          >
            <span className={styles['back-label-full']}>
              <Translation i18nKey="silver-alpha-topbar.back" ns="components" />
            </span>
            <span className={styles['back-label-short']}>
              <Translation
                i18nKey="silver-alpha-topbar.back-short"
                ns="components"
              />
            </span>
          </Button>
          <div className={styles.brand}>
            <span className={styles['brand-mark']}>
              <AppIcon icon="Shield" size="sm" />
            </span>
            <div className={styles['brand-copy']}>
              <span className={styles['brand-name']}>Silver Alpha</span>
              <span className={styles['brand-subtitle']}>Asset Management</span>
            </div>
          </div>
        </div>
      </header>
      <LeaveModalRenderer
        maxWidth={false}
        PaperProps={{ className: styles.paper }}
        data-testid="silver-alpha-leave-modal"
      >
        <CloseButton className={styles.close} onClick={onCancelLeave} />
        <div className={styles.body}>
          <Heading
            className={styles['leave-title']}
            title={
              <Translation i18nKey="silver-alpha-leave.title" ns="components" />
            }
            variant="h2"
          />
          <div className={styles.copy}>
            <p className={styles.message}>
              <Translation
                i18nKey="silver-alpha-leave.body-leave"
                ns="components"
              />
            </p>
            <p className={styles.message}>
              <Translation
                i18nKey="silver-alpha-leave.body-platform"
                ns="components"
              />
            </p>
          </div>
          <div className={styles.buttons}>
            <Button
              className={styles['leave-action']}
              color="secondary"
              data-testid="silver-alpha-leave-cancel-button"
              onClick={onCancelLeave}
              sx={{ textTransform: 'none' }}
              variant="outlined"
            >
              <Translation i18nKey="silver-alpha-leave.stay" ns="components" />
            </Button>
            <Button
              className={styles['leave-action']}
              color="secondary"
              data-testid="silver-alpha-leave-confirm-button"
              onClick={onConfirmLeave}
              sx={{ textTransform: 'none' }}
              variant="contained"
            >
              <Translation i18nKey="silver-alpha-leave.confirm" ns="components" />
            </Button>
          </div>
        </div>
      </LeaveModalRenderer>
    </>
  ),
});
